import { useTranslation } from "react-i18next";
import { Grid, Button, Skeleton } from "@mui/material";
import MUIDataTable from "mui-datatables";
import NoMatchImage from "../../components/common/NoMatchImage/noMatchImage";
import { NoInfoComponent } from "../../components/common/NoInfoImage/noInfoComponent";
import { useEffect, useState } from "react";

import { getUser, getUserStatus } from "../userSlice";
import { useSelector } from "react-redux";
import { CODES } from "../../consts/codes";
import {
  avisosFields,
  updateNSSAndNSSUnificadoValue,
} from "../../consts/avisosFields";

import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import { FilterInfonavitRequests } from "../../components/common/infonavit/filterInfonavitRequests";
import CustomTablePagination from "../../components/DataTable/CustomTablePagination";
import { GetDynamicReportConf } from "../../services/user/infonavit/adminDynamicReport";
import {
  GetLocalAvisos,
  GetEmployeesReport,
} from "../../services/user/infonavit/infonavitRequests";
import { GetCredentialsNrps } from "../../services/user/infonavit/adminCredentials";
import { formatMxCurrency } from "../../utils/functions";

export const InfonavitLocalRequests = () => {
  const userInfo = useSelector(getUser);
  const userStatus = useSelector(getUserStatus);

  const [data, setData] = useState([]);
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [noContent, setNoContent] = useState(false);

  const [nrpList, setNrpList] = useState([]);
  const [filterPayload, setFilterPayload] = useState(null);

  const { t } = useTranslation();

  const [columns, setColumns] = useState([]);

  const options = {
    filterType: "dropdown",
    print: "false",
    filter: false,
    download: false,
    search: false,
    selectToolbarPlacement: "none",
    selectableRows: "none",
    responsive: "standard",
    searchPlaceholder: t("App.infonavitRequests.table.filterPlaceholder"),
    elevation: 0,
    viewColumns: false,
    searchOpen: true,
    rowsPerPageOptions: [10, 15, 20],
    textLabels: {
      body: {
        noMatch: noContent ? (
          <NoMatchImage />
        ) : isLoadingData ? (
          <Skeleton />
        ) : (
          <NoInfoComponent type="dynamic" text="" />
        ),
        toolTip: "Sort",

        columnHeaderTooltip: (column) =>
          t("App.buttonLabels.order", { label: column.label }),
      },
      pagination: {
        next: t("App.userDetail.next"),
        previous: t("App.userDetail.previous"),
        rowsPerPage: t("App.userDetail.resultsPerPage"),
        displayRows: t("App.userDetail.of"),
      },
    },
  };

  /**
   * Carga los usuarios y NRPs
   */
  useEffect(() => {
    if (userStatus !== "succeeded") return;

    fetchConfiguration();

    if (userInfo?.nameRole === "Infocloud Admin") {
      fetchNrps();
    } else {
      const userNrps = userInfo?.nrps ?? [];

      setNrpList(
        userNrps.map((nrp) => {
          return { label: nrp, value: nrp };
        })
      );
    }
  }, [userStatus]);

  /**
   * Función que consulta y settea la información de los NRPs
   */
  const fetchNrps = async () => {
    try {
      const mainUser = userInfo.mainUser;
      const {
        status,
        data: { responseStatus, response },
      } = await GetCredentialsNrps(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setNrpList(
          response.map((row, index) => {
            return { label: row.nrp, value: row.nrp };
          })
        );
      } else {
        setNrpList([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  /**
   * Función que consulta y settea las columnas de la tabla
   */
  const fetchConfiguration = async () => {
    try {
      const mainUser = userInfo.mainUser;

      setIsLoadingData(true);

      const {
        status,
        data: { responseStatus, response },
      } = await GetDynamicReportConf(mainUser);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        let rpFields = avisosFields;
        const originalReportFields = response?.reportFields ?? [];

        rpFields =
          originalReportFields.length < 1
            ? rpFields
            : rpFields.filter((field) =>
                originalReportFields.includes(field.name)
              );

        rpFields = rpFields.map((field, index) => {
          return {
            ...field,
            options: {
              filter: false,
              sort: true,
              searchable: true,
            },
          };
        });

        setColumns(rpFields);
      } else {
        setColumns([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Consulta los empleados (avisos) descargados de Infonavit
   */
  const fetchEmployeesAvisos = async (payload) => {
    try {
      setIsLoadingData(true);

      payload = {
        ...payload,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetLocalAvisos(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        const responseData = response.map((i) => {
          i.facDescuento = formatMxCurrency(i?.facDescuento ?? "0");
          i.monDescuento = formatMxCurrency(i?.monDescuento ?? "0");

          return i;
        });

        // Update NSS and NSSUnificado values ​​if applicable
        setData(
          responseData.map((row, index) => updateNSSAndNSSUnificadoValue(row))
        );
        setUnfilteredData(responseData);
      } else {
        setData([]);
        setUnfilteredData([]);
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    } finally {
      setIsLoadingData(false);
    }
  };

  /**
   * Limpia los filtros
   */
  const cleanFilters = () => {
    setData([]);
    setUnfilteredData([]);
  };

  /**
   * Descarga del reporte de empleados
   */
  const handleDownloadReport = async (isExcel) => {
    try {
      const payload = {
        ...filterPayload,
        isExcel,
        mainUser: userInfo.mainUser,
      };

      const {
        status,
        data: { responseStatus, response },
      } = await GetEmployeesReport(payload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        if (response?.fileContent) {
          const a = document.createElement("a");
          a.href = "data:image/png;base64," + response?.fileContent;
          a.download = response?.fileName;
          a.click();
        }
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  return (
    <Grid
      container
      gap={2}
      paddingBottom="2rem !important"
      padding={{
        xs: "1.5rem",
        sm: "2.25rem 3rem",
        md: "2.25rem 3rem",
        lg: "4.5rem",
        xl: "4.5rem",
      }}
    >
      <Grid item xs="12">
        <p className="display-large__primary-one">
          {t("App.infonavitRequests.title")}
        </p>
      </Grid>

      <Grid item xs="12">
        <Grid
          container
          gap={2}
          padding="2rem"
          marginBottom="2rem"
          borderRadius="8px"
          style={{ backgroundColor: "white" }}
        >
          <Grid item xs="12">
            <Grid container direction="row" alignItems="center">
              <Grid item>
                <ManageAccountsIcon className="accordion__upload__consultIcon" />
              </Grid>

              <Grid item className="col-flex">
                <p
                  className="display-small__primary-one"
                  style={{ paddingLeft: "1rem" }}
                >
                  {t("App.infonavitRequests.subtitle")}
                </p>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs="12">
            <FilterInfonavitRequests
              handleFilters={fetchEmployeesAvisos}
              cleanFilters={cleanFilters}
              setNoContent={setNoContent}
              setFilterPayload={setFilterPayload}
              unfilteredData={unfilteredData}
              nrpList={nrpList}
              data={data}
            />
          </Grid>

          <Grid item xs="12">
            <MUIDataTable
              className="dataTable__base datatable-style dataTable--modern"
              style={{ marginTop: "5%", display: "none" }}
              components={{
                TableFooter: (props) => (
                  <CustomTablePagination paginationProps={props} />
                ),
              }}
              data={data}
              columns={columns}
              options={options}
            />
          </Grid>

          <Grid item xs="12">
            <Grid container gap={2} className="col-flex" padding="2rem">
              <Grid item>
                <Button
                  className="btn__filled__blue"
                  onClick={() => {
                    handleDownloadReport(true);
                  }}
                  disabled={!filterPayload}
                >
                  {t("App.infonavitRequests.btnGroup.exportExcel")}
                </Button>
              </Grid>

              <Grid item>
                <Button
                  className="btn__filled__blue"
                  onClick={() => {
                    handleDownloadReport(false);
                  }}
                  disabled={!filterPayload}
                >
                  {t("App.infonavitRequests.btnGroup.exportTxt")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
